import configs from "../utils/configs";
import { getAuthHeaders } from "../utils/getToken";

const VALID_PRESETS = ["maximum", "balanced", "quality", "minimal"];

const ensurePreset = preset => {
  if (VALID_PRESETS.includes(preset)) {
    return preset;
  }

  throw new Error(`Invalid preset: ${preset}. Valid presets are: ${VALID_PRESETS.join(", ")}`);
};

export class AssetOptimizationService {
  constructor() {
    this.baseUrl = `${configs.PERSISTENCE_API_URL}/assets`;
  }

  async getHeaders(url, method = "GET") {
    const authHeaders = await getAuthHeaders(url, method);
    if (!authHeaders.Authorization) {
      throw new Error("Authentication token is required to optimize assets.");
    }

    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...authHeaders
    };
  }

  async optimize(assetId, preset = "balanced") {
    const selectedPreset = ensurePreset(preset);
    const url = `${this.baseUrl}/${assetId}/optimize`;
    const headers = await this.getHeaders(url, "POST");

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({ preset: selectedPreset })
    });

    if (response.status === 202) {
      const data = await response.json();
      return {
        status: "processing",
        jobId: data.jobId,
        preset: selectedPreset
      };
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Optimization failed with status ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = window.URL?.createObjectURL ? window.URL.createObjectURL(blob) : null;

    return {
      status: "completed",
      blob,
      blobUrl,
      size: blob.size,
      preset: selectedPreset,
      contentType: response.headers.get("Content-Type") || "model/gltf-binary"
    };
  }

  async pollForCompletion(jobId, preset = "balanced", options = {}) {
    const selectedPreset = ensurePreset(preset);
    const { maxAttempts = 150, interval = 2000 } = options;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const status = await this.getJobStatus(jobId);

      if (status.state === "completed" && status.result?.success) {
        return {
          tempAzureUrl: status.result.tempAzureUrl,
          size: status.result.optimizedSize,
          preset: selectedPreset,
          contentType: status.result.contentType || "model/gltf-binary"
        };
      }

      if (status.state === "failed") {
        throw new Error(status.failedReason || "Optimization failed");
      }

      await new Promise(resolve => setTimeout(resolve, interval));
    }

    throw new Error("Optimization is taking longer than expected. Please try again later.");
  }

  async getJobStatus(jobId) {
    const url = `${this.baseUrl}/optimize/jobs/${jobId}`;
    const headers = await this.getHeaders(url, "GET");

    const response = await fetch(url, {
      method: "GET",
      headers
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `Failed to retrieve job status (${response.status})`);
    }

    return data;
  }

  async confirm(jobId, assetId, preset, size, contentType = "model/gltf-binary", createCopy = false, collectionId) {
    const selectedPreset = ensurePreset(preset);
    const url = `${this.baseUrl}/optimize/jobs/${jobId}/confirm`;
    const headers = await this.getHeaders(url, "POST");

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({
        preset: selectedPreset,
        size: size || 0,
        contentType,
        createCopy,
        assetId,
        ...(collectionId ? { collectionId } : {})
      })
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const error = new Error(data.error || `Failed to confirm optimization (${response.status})`);
      error.status = response.status;
      throw error;
    }

    return data.data || data;
  }

  async cancel(jobId) {
    const url = `${this.baseUrl}/optimize/jobs/${jobId}/cancel`;
    const headers = await this.getHeaders(url, "POST");

    const response = await fetch(url, {
      method: "POST",
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to cancel optimization (${response.status})`);
    }

    return response.json();
  }

  revokeBlobUrl(url) {
    if (typeof url === "string" && url.startsWith("blob:") && window.URL?.revokeObjectURL) {
      window.URL.revokeObjectURL(url);
    }
  }
}
